import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import LinkPreview from '@/components/common/LinkPreview';
import { Profile } from '@/types/supabase';

interface ChatMessageContentProps {
  content: string;
  channelMembers: Profile[];
}

const ChatMessageContent: React.FC<ChatMessageContentProps> = ({ content, channelMembers }) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const urls = content.match(urlRegex);

  // Extract YouTube video ID from URL
  const extractYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/shorts\/([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  // Check if content contains YouTube URLs
  const containsYouTube = (text: string): boolean => {
    return /(?:youtube\.com|youtu\.be)/.test(text);
  };

  // Render YouTube embed
  const renderYouTubeEmbed = (videoId: string) => (
    <div className="my-4 max-w-xl">
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          className="absolute top-0 left-0 w-full h-full rounded-lg border border-border"
          src={`https://www.youtube.com/embed/${videoId}`}
          title="YouTube video player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    </div>
  );

  // Process content to handle YouTube URLs
  const processedContent = content.split('\n').map((line, index) => {
    const urls = line.match(urlRegex);
    
    if (urls && urls.some(url => containsYouTube(url))) {
      return (
        <div key={index}>
          {urls.map((url, urlIndex) => {
            const videoId = extractYouTubeId(url);
            if (videoId) {
              return (
                <React.Fragment key={urlIndex}>
                  {renderYouTubeEmbed(videoId)}
                </React.Fragment>
              );
            } else {
              // For non-YouTube URLs, use the original LinkPreview
              return <LinkPreview key={urlIndex} url={url} />;
            }
          })}
          {/* Render the rest of the line without the URLs */}
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || '');
                return !inline && match ? (
                  <SyntaxHighlighter
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  >
                    {String(children).replace(/\n$/, '')}
                  </SyntaxHighlighter>
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
              img: ({node, ...props}) => <img className="max-w-full h-auto rounded-md my-2" {...props} />,
              a: ({node, ...props}) => {
                // Skip rendering links that are YouTube URLs since we handle them separately
                if (containsYouTube(props.href || '')) {
                  return null;
                }
                return <LinkPreview url={props.href || ''} />;
              }
            }}
          >
            {line.replace(urlRegex, '').trim()}
          </ReactMarkdown>
        </div>
      );
    }
    
    // For lines without YouTube URLs, use the original rendering
    return (
      <ReactMarkdown
        key={index}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            return !inline && match ? (
              <SyntaxHighlighter
                style={vscDarkPlus}
                language={match[1]}
                PreTag="div"
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          img: ({node, ...props}) => <img className="max-w-full h-auto rounded-md my-2" {...props} />,
          a: ({node, ...props}) => <LinkPreview url={props.href || ''} />
        }}
      >
        {line}
      </ReactMarkdown>
    );
  });

  return (
    <div className="prose prose-sm dark:prose-invert max-w-none break-words">
      {processedContent}
    </div>
  );
};

export default ChatMessageContent;